import type { LlmProviderSettings, RuntimeProvider } from "@automatry/schemas";

import { withAuthHeaders } from "../browser/apiAuthHeaders";

const LLM_PROVIDER_SETTINGS_ENDPOINT = "/api/settings/llm-provider";

export type LlmSettingsSource = "registry_default" | "env_bootstrap" | "settings_doc";

export type ReasoningEffortSource =
  | "settings_doc"
  | "registry_default"
  | "provider_default";

export interface LlmReasoningSettings {
  activeReasoningEffort: string | null;
  effectiveReasoningEffort: string;
  reasoningEffortSource: ReasoningEffortSource;
  supportedReasoningEfforts: readonly string[];
}

export interface ApprovedLlmPair {
  provider: RuntimeProvider;
  modelId: string;
  displayName: string;
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
}

export interface EffectiveLlmSettings {
  provider: RuntimeProvider;
  modelId: string;
  revision: number;
  updatedAt: string | null;
  source: LlmSettingsSource;
  invalidReason?: string;
}

export interface LlmProviderSettingsResponse {
  ok: true;
  settings: LlmProviderSettings | null;
  approvedPairs: readonly ApprovedLlmPair[];
  effective: EffectiveLlmSettings;
  canEdit: boolean;
  recentRevisions: readonly LlmProviderSettings[];
  activeReasoningEffort?: string | null;
  effectiveReasoningEffort?: string;
  reasoningEffortSource?: ReasoningEffortSource;
  supportedReasoningEfforts?: readonly string[];
}

export interface SaveLlmProviderSettingsInput {
  activeProvider: RuntimeProvider;
  activeModelId: string;
  note: string;
  expectedRevision: number;
  activeReasoningEffort?: string | null;
}

export interface SaveLlmProviderSettingsResponse {
  ok: true;
  settings: LlmProviderSettings;
  effective: EffectiveLlmSettings;
  recentRevisions: readonly LlmProviderSettings[];
  activeReasoningEffort?: string | null;
  effectiveReasoningEffort?: string;
  reasoningEffortSource?: ReasoningEffortSource;
  supportedReasoningEfforts?: readonly string[];
}

export async function getLlmProviderSettings(): Promise<LlmProviderSettingsResponse> {
  return requestJson<LlmProviderSettingsResponse>(LLM_PROVIDER_SETTINGS_ENDPOINT, {
    method: "GET",
    headers: { accept: "application/json" }
  });
}

export async function saveLlmProviderSettings(
  input: SaveLlmProviderSettingsInput
): Promise<SaveLlmProviderSettingsResponse> {
  return requestJson<SaveLlmProviderSettingsResponse>(LLM_PROVIDER_SETTINGS_ENDPOINT, {
    method: "PUT",
    headers: {
      accept: "application/json",
      "content-type": "application/json"
    },
    body: JSON.stringify(input)
  });
}

export class LlmSettingsApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly payload: unknown;

  constructor(status: number, payload: unknown) {
    super(apiErrorMessage(status, payload));
    this.name = "LlmSettingsApiError";
    this.status = status;
    this.code = apiErrorCode(payload);
    this.payload = payload;
  }
}

async function requestJson<T>(input: RequestInfo | URL, init: RequestInit): Promise<T> {
  const headers = await withAuthHeaders(init.headers);
  const response = await fetch(input, { ...init, headers });
  const payload = await readJsonPayload(response);

  if (!response.ok) {
    throw new LlmSettingsApiError(response.status, payload);
  }

  return payload as T;
}

async function readJsonPayload(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch (error) {
    throw new Error(
      `LLM provider settings API returned invalid JSON (HTTP ${response.status}): ${
        error instanceof Error ? error.message : "unknown parse error"
      }`,
      { cause: error }
    );
  }
}

function apiErrorMessage(status: number, payload: unknown): string {
  const record = asRecord(payload);
  const serverMessage =
    typeof record?.error === "string"
      ? record.error
      : typeof record?.message === "string"
        ? record.message
        : undefined;

  return serverMessage
    ? `LLM provider settings request failed (HTTP ${status}): ${serverMessage}`
    : `LLM provider settings request failed with HTTP ${status}.`;
}

function apiErrorCode(payload: unknown): string | undefined {
  const code = asRecord(payload)?.code;

  return typeof code === "string" ? code : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : undefined;
}
